/**
 * Centralized data loading service to prevent manual refresh requirements
 * Handles automatic data loading, caching, and state management
 */

import userActivityService from './userActivityService.js';

class DataLoadingService {
  constructor() {
    this.cache = new Map();
    this.loadingStates = new Map();
    this.listeners = new Map();
    this.retryAttempts = new Map();
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second
  }

  /**
   * Register a component for automatic data loading
   */
  registerComponent(componentId, loadFunction, dependencies = []) {
    console.log(`📋 Registering component: ${componentId}`);
    
    this.listeners.set(componentId, {
      loadFunction,
      dependencies,
      lastLoaded: null,
      isActive: true
    });

    // Trigger initial load
    this.loadData(componentId);
  }

  /**
   * Unregister a component
   */
  unregisterComponent(componentId) {
    console.log(`📋 Unregistering component: ${componentId}`);
    this.listeners.delete(componentId);
    this.cache.delete(componentId);
    this.loadingStates.delete(componentId);
    this.retryAttempts.delete(componentId);
  }

  /**
   * Load data for a specific component with retry logic
   */
  async loadData(componentId, force = false) {
    const listener = this.listeners.get(componentId);
    if (!listener || !listener.isActive) {
      console.log(`📋 Component ${componentId} not active, skipping load`);
      return;
    }

    // Check if already loading
    if (this.loadingStates.get(componentId) && !force) {
      console.log(`📋 Component ${componentId} already loading, skipping`);
      return;
    }

    // Check cache if not forcing reload
    if (!force && this.cache.has(componentId)) {
      const cached = this.cache.get(componentId);
      const cacheAge = Date.now() - cached.timestamp;
      if (cacheAge < 30000) { // 30 seconds cache
        console.log(`📋 Using cached data for ${componentId}`);
        return cached.data;
      }
    }

    console.log(`📋 Loading data for component: ${componentId}`);
    this.loadingStates.set(componentId, true);

    try {
      const data = await listener.loadFunction();
      
      // Cache the result
      this.cache.set(componentId, {
        data,
        timestamp: Date.now()
      });

      // Reset retry attempts on success
      this.retryAttempts.delete(componentId);
      
      listener.lastLoaded = Date.now();
      console.log(`✅ Data loaded successfully for ${componentId}`);
      
      return data;
    } catch (error) {
      console.error(`❌ Failed to load data for ${componentId}:`, error);
      
      // Implement retry logic
      const attempts = this.retryAttempts.get(componentId) || 0;
      if (attempts < this.maxRetries) {
        this.retryAttempts.set(componentId, attempts + 1);
        console.log(`🔄 Retrying load for ${componentId} (attempt ${attempts + 1}/${this.maxRetries})`);
        
        setTimeout(() => {
          this.loadData(componentId, true);
        }, this.retryDelay * (attempts + 1)); // Exponential backoff
      } else {
        console.error(`❌ Max retries exceeded for ${componentId}`);
        this.retryAttempts.delete(componentId);
      }
      
      throw error;
    } finally {
      this.loadingStates.set(componentId, false);
    }
  }

  /**
   * Invalidate cache for specific component or all components
   */
  invalidateCache(componentId = null) {
    if (componentId) {
      console.log(`📋 Invalidating cache for ${componentId}`);
      this.cache.delete(componentId);
    } else {
      console.log(`📋 Invalidating all cache`);
      this.cache.clear();
    }
  }

  /**
   * Refresh data for specific component or all active components
   */
  async refreshData(componentId = null) {
    if (componentId) {
      console.log(`📋 Refreshing data for ${componentId}`);
      this.invalidateCache(componentId);
      return await this.loadData(componentId, true);
    } else {
      console.log(`📋 Refreshing data for all components`);
      this.invalidateCache();
      
      const refreshPromises = [];
      for (const [id, listener] of this.listeners.entries()) {
        if (listener.isActive) {
          refreshPromises.push(this.loadData(id, true));
        }
      }
      
      return await Promise.allSettled(refreshPromises);
    }
  }

  /**
   * Check if component is currently loading
   */
  isLoading(componentId) {
    return this.loadingStates.get(componentId) || false;
  }

  /**
   * Get cached data for component
   */
  getCachedData(componentId) {
    const cached = this.cache.get(componentId);
    return cached ? cached.data : null;
  }

  /**
   * Set component active/inactive state
   */
  setComponentActive(componentId, isActive) {
    const listener = this.listeners.get(componentId);
    if (listener) {
      listener.isActive = isActive;
      console.log(`📋 Component ${componentId} set to ${isActive ? 'active' : 'inactive'}`);
    }
  }

  /**
   * Listen for navigation changes and refresh relevant components
   */
  handleNavigationChange(newPath, newParams = {}) {
    console.log(`📋 Navigation change detected: ${newPath}`);
    
    // Determine which components need to refresh based on the new path
    const componentsToRefresh = this.getComponentsForPath(newPath);
    
    componentsToRefresh.forEach(componentId => {
      this.refreshData(componentId);
    });
  }

  /**
   * Get components that should refresh for a given path
   */
  getComponentsForPath(path) {
    const pathComponentMap = {
      '/project-management': ['project-data', 'project-tasks', 'project-team'],
      '/kanban-board': ['kanban-board', 'kanban-cards'],
      '/dashboard': ['dashboard-stats', 'dashboard-projects', 'dashboard-notifications'],
      '/role-based-dashboard': ['dashboard-stats', 'dashboard-projects', 'dashboard-notifications']
    };

    // Find matching path patterns
    for (const [pathPattern, components] of Object.entries(pathComponentMap)) {
      if (path.includes(pathPattern)) {
        return components;
      }
    }

    return [];
  }

  /**
   * Clear all data and reset service
   */
  reset() {
    console.log(`📋 Resetting data loading service`);
    this.cache.clear();
    this.loadingStates.clear();
    this.retryAttempts.clear();
    
    // Keep listeners but mark them as inactive
    for (const [id, listener] of this.listeners.entries()) {
      listener.isActive = false;
    }
  }
}

// Create singleton instance
const dataLoadingService = new DataLoadingService();

export default dataLoadingService;
